<template>
  <div v-if="visible" class="bg-white rounded-3 p-3 shadow-sm mb-3">
    <div class="d-flex align-items-center gap-2 mb-2">
      <div class="promo-icon flex-shrink-0 rounded-circle d-flex align-items-center justify-content-center">
        <i class="fa-solid fa-gift" />
      </div>
      <div class="flex-grow-1 min-w-0">
        <div class="fw-semibold text-truncate" style="font-size: 14px;">{{ promotionName || $t('_promotion.Promotion') }}</div>
        <div class="text-muted" style="font-size: 11px;">
          <i class="fa-solid fa-layer-group me-1" />{{ tierMode === 'single' ? $t('_promotion.Pick any one tier') : $t('_promotion.All reached tiers') }}
        </div>
      </div>
      <span v-if="loading" class="text-muted flex-shrink-0"><i class="fa-solid fa-spinner fa-spin" /></span>
    </div>

    <template v-if="!loading">
      <!-- single: ใช้สิทธิ์ไปแล้ว (เลือกได้ 1 อย่าง/บิล) -->
      <div v-if="promoUsed" class="promo-used d-flex align-items-center gap-2 rounded-3 px-3 py-2 mb-2" style="font-size: 12px;">
        <i class="fa-solid fa-circle-check" />{{ $t('_promotion.Promotion used') }}
      </div>

      <!-- Tier cards -->
      <div
        v-for="tier in visibleTiers"
        :key="tier.promotion_item_id"
        class="promo-tier rounded-3 px-2 py-2 mb-2"
        :class="{ 'promo-tier--met': tier.is_met }"
      >
        <div class="d-flex justify-content-between align-items-center gap-2">
          <span class="min-w-0" style="font-size: 13px;">
            <i
              class="me-1"
              :class="tier.is_met ? 'fa-solid fa-circle-check text-success' : 'fa-regular fa-circle text-muted'"
            />
            <span class="text-muted" style="font-size: 11.5px;">{{ $t('_promotion.Reach') }} {{ thresholdLabel(tier) }} ·</span>
            <span class="fw-semibold">
              {{ $ln(tier, 'reward_product_name') }}<span v-if="tier.free_quantity > 1"> × {{ tier.free_quantity }}</span>
            </span>
            <span v-if="tier.is_pwp" class="badge bg-info-subtle text-info ms-1">{{ $storeIndex.formatThaiCurrency(tier.reward_price) }}</span>
            <span v-else class="badge bg-success-subtle text-success ms-1">{{ $t('_promotion.Free') }}</span>
            <span v-if="isClaimed(tier)" class="badge bg-success-subtle text-success ms-1">
              <i class="fa-solid fa-check me-1" />{{ $t('_promotion.claimed') }}
            </span>
            <span v-if="!tier.is_met && showStockEarly && !tier.reward_in_stock" class="badge bg-secondary ms-1">{{ $t('_promotion.Currently out of stock') }}</span>
          </span>

          <!-- met & ยังไม่รับ → ปุ่มรับ (GWP ฟรี / PWP ราคาพิเศษ) -->
          <span
            v-if="tier.is_met && !isClaimed(tier) && !finalized && !tier.reward_in_stock"
            class="badge bg-secondary flex-shrink-0"
          >
            {{ $t('_promotion.Out of stock') }}
          </span>
          <button
            v-else-if="tier.is_met && !isClaimed(tier) && !finalized"
            type="button"
            class="btn btn-sm rounded-pill px-3 flex-shrink-0 fw-semibold"
            :class="tier.is_pwp ? 'btn-info text-white' : 'btn-success'"
            :disabled="busy"
            @click="onClaimClick(tier)"
          >
            <i class="fa-solid fa-gift me-1" />{{ tier.is_pwp ? $storeIndex.formatThaiCurrency(tier.reward_price) : $t('_promotion.Claim now') }}
          </button>
        </div>

        <!-- ขั้นที่ยังไม่ถึง → progress bar + เหลืออีกเท่าไหร่ -->
        <template v-if="!tier.is_met">
          <div class="progress mt-2" style="height: 6px;">
            <div class="progress-bar bg-success" :style="{ width: tierProgress(tier) + '%' }" />
          </div>
          <div class="d-flex justify-content-between align-items-center mt-1" style="font-size: 11px;">
            <span class="text-muted">{{ baseLabel(tier) }} / {{ thresholdLabel(tier) }}</span>
            <span class="text-muted">
              {{ $t('_promotion.Buy more') }} <b class="text-dark">{{ remainingLabel(tier) }}</b>
            </span>
          </div>
        </template>
      </div>

      <!-- Granted rewards on this bill (GWP added / PWP claimed) -->
      <div v-if="grantedRewards.length" class="border-top mt-2 pt-2">
        <div class="promo-section-title text-muted mb-1">{{ $t('_promotion.Promo rewards') }}</div>
        <div
          v-for="r in grantedRewards"
          :key="r.reference"
          class="d-flex align-items-center gap-2 bg-light rounded-3 px-2 py-2 mb-1"
          style="font-size: 13px;"
        >
          <div class="reward-ic flex-shrink-0 rounded-circle d-flex align-items-center justify-content-center" :class="r.is_pwp ? 'reward-ic--pwp' : 'reward-ic--gwp'">
            <i :class="r.is_pwp ? 'fa-solid fa-tag' : 'fa-solid fa-gift'" />
          </div>
          <div class="flex-grow-1 min-w-0 text-truncate">
            {{ $ln(r, 'product_name') }}<span v-if="r.quantity > 1" class="text-muted"> × {{ r.quantity }}</span>
          </div>
          <span class="badge flex-shrink-0" :class="r.is_pwp ? 'bg-info-subtle text-info' : 'bg-success-subtle text-success'">
            {{ r.is_pwp ? $storeIndex.formatThaiCurrency(r.total) : $t('_promotion.Free') }}
          </span>
          <button
            v-if="!finalized"
            type="button"
            class="btn btn-sm btn-link text-danger p-0 flex-shrink-0"
            :title="$t('_promotion.Cancel reward?')"
            :disabled="busy"
            @click="cancelReward(r)"
          >
            <i class="fa-solid fa-xmark" />
          </button>
        </div>
      </div>

      <!-- reward ที่ถูกถอดแบบไม่คืนสต๊อก (sync ตอนครัวทำแล้ว) → เช็คกับครัวแล้วกดคืนได้ที่นี่
           (reward order หายจาก KDS ไปแล้ว — แผงนี้คือทางเดียวที่กดคืนทีหลังได้) -->
      <div v-if="unrestockedRewards.length" class="border-top mt-2 pt-2">
        <div class="promo-section-title text-muted mb-1">
          <i class="fa-solid fa-rotate-left me-1" />{{ $t('_promotion.Removed rewards not restocked') }}
        </div>
        <div
          v-for="r in unrestockedRewards"
          :key="r.reference"
          class="promo-unrestock d-flex align-items-center gap-2 rounded-3 px-2 py-2 mb-1"
          style="font-size: 13px;"
        >
          <div class="reward-ic reward-ic--off flex-shrink-0 rounded-circle d-flex align-items-center justify-content-center">
            <i class="fa-solid fa-ban" />
          </div>
          <div class="flex-grow-1 min-w-0 text-truncate text-muted">
            {{ $ln(r, 'product_name') }}<span v-if="r.quantity > 1"> × {{ r.quantity }}</span>
          </div>
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary rounded-pill px-3 flex-shrink-0"
            :disabled="busy"
            @click="restockReward(r)"
          >
            <i class="fa-solid fa-rotate-left me-1" />{{ $t('Restock') }}
          </button>
        </div>
      </div>
    </template>

    <!-- config reward → เลือก variant/modifier ก่อนรับ (reuse loyalty modal) -->
    <reward-select-modal
      v-if="configTier"
      ref="configModal"
      :reward-product-id="configTier.reward_product_id"
      :reward-name="$ln(configTier, 'reward_product_name')"
      :loading="busy"
      @confirm="onConfigConfirm"
      @close="configTier = null"
    />
  </div>
</template>

<script lang="js">
import { Promotion } from '@/services/endpoint/promotion.service';
import RewardSelectModal from '@/components/pos/loyalty/RewardSelectModal.vue';

export default {
  name: 'PromotionBillPanel',
  components: { RewardSelectModal },
  props: {
    billId: { type: [Number, String], default: 0 },
    // Bill must not be paid/voided/refunded before claim/cancel is allowed.
    finalized: { type: Boolean, default: false }
  },
  emits: ['changed'],
  data() {
    return {
      visible: false,
      loading: false,
      busy: false,
      promotionName: '',
      tierMode: 'single',
      showStockEarly: true,
      configTier: null,
      tiers: [],
      grantedRewards: [],
      claimedTierIds: [],
      unrestockedRewards: []
    };
  },
  computed: {
    // single mode: ใช้สิทธิ์ไปแล้ว (รับ reward บนบิลแล้ว) → ปิดการรับเพิ่ม + ซ่อน progress ขั้นอื่น
    promoUsed() {
      return this.tierMode === 'single' && this.claimedTierIds.length > 0;
    },
    visibleTiers() {
      if (!this.promoUsed) return this.tiers;
      return this.tiers.filter((t) => this.isClaimed(t));
    }
  },
  watch: {
    billId() { this.fetch(); }
  },
  mounted() {
    this.fetch();
  },
  methods: {
    // Server is the source of truth; FE only renders.
    applyData(d) {
      const data = d || {};
      this.promotionName = data.promotion_name || '';
      this.tierMode = data.promotion_tier_mode || 'single';
      this.showStockEarly = data.promotion_show_stock_early !== false;
      this.tiers = data.tiers || [];
      this.grantedRewards = data.granted_rewards || [];
      this.claimedTierIds = (data.claimed_tier_ids || []).map(Number);
      this.unrestockedRewards = data.unrestocked_rewards || [];
      // Show the panel when there's an active promotion or any granted/unrestocked reward to manage.
      this.visible = !!data.has_promotion || this.grantedRewards.length > 0 || this.unrestockedRewards.length > 0;
    },
    isClaimed(tier) {
      return this.claimedTierIds.includes(Number(tier.promotion_item_id));
    },
    // ---------- display helpers (progress bar ต่อขั้น) ----------
    tierProgress(tier) {
      const threshold = parseFloat(tier.threshold) || 0;
      if (threshold <= 0) return 100;
      return Math.min(100, Math.round(((parseFloat(tier.base) || 0) / threshold) * 100));
    },
    thresholdLabel(tier) {
      return tier.condition_type === 'quantity'
        ? (parseFloat(tier.threshold) || 0) + ' ' + this.$t('_promotion.pcs')
        : this.$storeIndex.formatThaiCurrency(tier.threshold);
    },
    baseLabel(tier) {
      return tier.condition_type === 'quantity'
        ? (parseFloat(tier.base) || 0) + ' ' + this.$t('_promotion.pcs')
        : this.$storeIndex.formatThaiCurrency(tier.base);
    },
    remainingLabel(tier) {
      return tier.condition_type === 'quantity'
        ? tier.remaining + ' ' + this.$t('_promotion.pcs')
        : this.$storeIndex.formatThaiCurrency(tier.remaining);
    },
    async fetch() {
      const id = this.billId;
      if (!id || String(id).startsWith('local-')) {
        this.visible = false;
        return;
      }
      this.loading = true;
      try {
        const res = await Promotion.billPromotion(id);
        this.applyData(res.data?.data ?? res.data ?? {});
      } catch (err) {
        // No permission / no promotion → hide the panel.
        this.visible = false;
      } finally {
        this.loading = false;
      }
    },
    async refresh() {
      await this.fetch();
      this.$emit('changed');
    },
    onClaimClick(tier) {
      if (!tier.reward_in_stock) return;
      // config reward → เลือก variant/modifier ก่อน
      if (tier.reward_is_config) {
        this.configTier = tier;
        return;
      }
      this.doClaim(tier, {});
    },
    onConfigConfirm(selection) {
      if (this.configTier) this.doClaim(this.configTier, selection || {});
      if (this.$refs.configModal) this.$refs.configModal.close();
      else this.configTier = null;
    },
    async doClaim(tier, selection) {
      this.busy = true;
      try {
        const payload = { promotion_item_id: tier.promotion_item_id };
        if (selection.product_id) payload.product_id = selection.product_id;
        if (selection.modifier_option_ids && selection.modifier_option_ids.length) {
          payload.modifier_option_ids = selection.modifier_option_ids;
        }
        // BE คืน panel ล่าสุดมากับ response อยู่แล้ว → อัปเดตตรงๆ ไม่ต้องยิง GET ซ้ำ
        const res = await Promotion.claim(this.billId, payload);
        this.applyData(res.data?.data ?? res.data ?? {});
        this.$toast.success('<B>' + this.$t('Success') + '</B>\n' + this.$t('_promotion.Reward added to bill'));
        if (this.$refs.configModal) this.$refs.configModal.close();
        else this.configTier = null;
        this.$emit('changed'); // parent รีเฟรชบิลแบบเงียบ (ยอด/รายการ)
      } catch (err) {
        this.$toast.warn(this.$t(err?.response?.data?.message) || this.$t('_promotion.Failed to claim reward'));
      } finally {
        this.busy = false;
      }
    },
    // คืนสต๊อก reward ที่ถูกถอดไว้แบบไม่คืน — ยืนยันก่อน (ควรเช็คกับครัวว่ายังไม่ได้ทำจริง)
    async restockReward(reward) {
      const result = await this.$swal({
        title: this.$t('_loyalty.Return ingredients to stock?'),
        text: this.$ln(reward, 'product_name'),
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('Restock'),
        cancelButtonText: this.$t('Close'),
        confirmButtonColor: '#198754'
      });
      if (!result.isConfirmed) return;

      this.busy = true;
      try {
        const res = await Promotion.restockReward(this.billId, reward.reference);
        this.applyData(res.data?.data ?? res.data ?? {});
        this.$toast.success('<B>' + this.$t('Success') + '</B>\n' + this.$t('_promotion.Ingredients restocked'));
        this.$emit('changed');
      } catch (err) {
        this.$toast.warn(err?.response?.data?.message || this.$t('_promotion.Failed to restock'));
      } finally {
        this.busy = false;
      }
    },
    async cancelReward(reward) {
      // reward = สินค้าจริงที่หักสต๊อก → ถามว่าคืนวัตถุดิบไหม (เหมือน cancel item / loyalty)
      const result = await this.$swal({
        title: this.$t('_promotion.Cancel reward?'),
        text: this.$t('_loyalty.Return ingredients to stock?'),
        icon: 'question',
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: this.$t('Restock'),
        denyButtonText: this.$t("Don't restock"),
        cancelButtonText: this.$t('Close'),
        confirmButtonColor: '#198754',
        denyButtonColor: '#6c757d'
      });
      if (result.isDismissed) return;
      const isRestock = result.isConfirmed;

      this.busy = true;
      try {
        const res = await Promotion.cancelReward(this.billId, reward.reference, isRestock);
        this.applyData(res.data?.data ?? res.data ?? {});
        this.$toast.success('<B>' + this.$t('Success') + '</B>\n' + this.$t('_promotion.Reward cancelled'));
        this.$emit('changed');
      } catch (err) {
        this.$toast.warn(err?.response?.data?.message || this.$t('_promotion.Failed to cancel reward'));
      } finally {
        this.busy = false;
      }
    }
  }
};
</script>

<style scoped>
.promo-icon {
  width: 34px;
  height: 34px;
  background: #d1e7dd;
  color: #198754;
  font-size: 15px;
}
.promo-used {
  background: #d1e7dd;
  color: #0f5132;
}
.promo-tier {
  border: 1px solid #e9ecef;
  background: #fff;
  transition: background-color .15s ease, border-color .15s ease;
}
.promo-tier--met {
  background: #f2fbf6;
  border-color: #badbcc;
}
.promo-section-title {
  font-size: 10.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .4px;
}
.reward-ic {
  width: 28px;
  height: 28px;
  font-size: 12px;
}
.reward-ic--gwp { background: #d1e7dd; color: #198754; }
.reward-ic--pwp { background: #cff4fc; color: #0aa2c0; }
.reward-ic--off { background: #e9ecef; color: #6c757d; }
.promo-unrestock {
  background: #fff9e8;
  border: 1px dashed #ffe69c;
}
.progress { background-color: #e9ecef; border-radius: 99px; }
.progress-bar { border-radius: 99px; transition: width .35s ease; }
</style>
